import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
} from "react";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Fab from "@material-ui/core/Fab";
import Typography from "@material-ui/core/Typography";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Chip } from "@material-ui/core";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import LabelIcon from "@material-ui/icons/Label";
import PeopleIcon from "@material-ui/icons/People";
import { MoreHoriz } from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TagModal from "../../components/TagModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ContactTagListModal from "../../components/ContactTagListModal";

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_TAGS":
      return [...state, ...action.payload];
    case "UPDATE_TAGS": {
      const tag = action.payload;
      const tagIndex = state.findIndex((s) => s.id === tag.id);
      if (tagIndex !== -1) {
        state[tagIndex] = tag;
        return [...state];
      } else {
        return [tag, ...state];
      }
    }
    case "DELETE_TAGS":
      return state.filter((tag) => tag.id !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  // Mobile
  mobileList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1),
  },
  card: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    overflow: "hidden",
    display: "flex",
    alignItems: "stretch",
  },
  cardColorBar: {
    width: 6,
    flexShrink: 0,
  },
  cardContent: {
    flex: 1,
    padding: theme.spacing(1.2, 1.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  cardBody: {
    flex: 1,
    minWidth: 0,
  },
  cardName: {
    fontWeight: 700,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  cardMeta: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  cardActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    gap: 2,
    paddingRight: theme.spacing(0.5),
  },
  mobileHeader: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: "100%",
    padding: theme.spacing(0, 0, 1),
  },
  mobileHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  fab: {
    position: "fixed",
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: 10,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.secondary,
    gap: theme.spacing(1),
  },
  colorDot: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    flexShrink: 0,
  },
}));

const Tags = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user, socket } = useContext(AuthContext);

  const [selectedTagContacts, setSelectedTagContacts] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [pageSize] = useState(50);
  const [selectedTagName, setSelectedTagName] = useState("");
  const [selectedTag, setSelectedTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [tags, dispatch] = useReducer(reducer, []);
  const [tagModalOpen, setTagModalOpen] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const fetchMoreTags = async () => {
        try {
          const { data } = await api.get("/tags/", {
            params: { searchParam, pageNumber, kanban: 0, limit: pageSize },
          });
          dispatch({ type: "LOAD_TAGS", payload: data.tags });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };

      if (pageNumber > 0) {
        setLoading(true);
        fetchMoreTags();
      }
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, pageSize]);

  useEffect(() => {
    const onCompanyTags = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_TAGS", payload: data.tag });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_TAGS", payload: +data.tagId });
      }
    };
    socket.on(`company${user.companyId}-tag`, onCompanyTags);
    return () => {
      socket.off(`company${user.companyId}-tag`, onCompanyTags);
    };
  }, [socket, user.companyId]);

  const handleOpenTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(true);
  };

  const handleCloseTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
    setPageNumber(1);
    dispatch({ type: "RESET" });
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    setTagModalOpen(true);
  };

  const handleShowContacts = (contacts, tag) => {
    setSelectedTagContacts(contacts);
    setContactModalOpen(true);
    setSelectedTagName(tag);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
    setSelectedTagContacts([]);
    setSelectedTagName("");
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toast.success(i18n.t("tags.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingTag(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prev) => prev + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const renderMobileCard = (tag) => {
    const contactCount = tag?.contacts?.length || 0;
    return (
      <div key={tag.id} className={classes.card}>
        <div className={classes.cardColorBar} style={{ background: tag.color }} />
        <div className={classes.cardContent}>
          <div className={classes.cardBody}>
            <div className={classes.cardName}>
              <span className={classes.colorDot} style={{ background: tag.color }} />
              {tag.name}
            </div>
            <div className={classes.cardMeta}>
              <PeopleIcon style={{ fontSize: 13 }} />
              {contactCount} {contactCount === 1 ? "contacto" : "contactos"}
              {contactCount > 0 && (
                <IconButton
                  size="small"
                  style={{ padding: 2 }}
                  onClick={() => handleShowContacts(tag?.contacts, tag)}
                >
                  <MoreHoriz style={{ fontSize: 16 }} />
                </IconButton>
              )}
            </div>
          </div>
          <div className={classes.cardActions}>
            <IconButton size="small" onClick={() => handleEditTag(tag)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                setConfirmModalOpen(true);
                setDeletingTag(tag);
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      </div>
    );
  };

  return (
    <MainContainer>
      {contactModalOpen && (
        <ContactTagListModal
          open={contactModalOpen}
          onClose={handleCloseContactModal}
          tag={selectedTagName}
        />
      )}
      <ConfirmationModal
        title={deletingTag && i18n.t("tags.confirmationModal.deleteTitle")}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteTag(deletingTag.id)}
      >
        {i18n.t("tags.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <TagModal
        open={tagModalOpen}
        onClose={handleCloseTagModal}
        aria-labelledby="form-dialog-title"
        tagId={selectedTag && selectedTag.id}
        kanban={0}
      />

      {isMobile ? (
        <>
          <MainHeader>
            <div className={classes.mobileHeader}>
              <div className={classes.mobileHeaderRow}>
                <Title>{i18n.t("tags.title")}</Title>
                <Typography variant="caption" color="textSecondary">
                  {tags.length} etiquetas
                </Typography>
              </div>
              <TextField
                fullWidth
                size="small"
                placeholder={i18n.t("contacts.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "gray" }} fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          </MainHeader>

          <Paper
            className={classes.mainPaper}
            variant="outlined"
            onScroll={handleScroll}
          >
            {tags.length === 0 && !loading ? (
              <div className={classes.emptyState}>
                <LabelIcon style={{ fontSize: 48, opacity: 0.3 }} />
                <Typography variant="body2">Sin etiquetas</Typography>
              </div>
            ) : (
              <div className={classes.mobileList}>
                {tags.map(renderMobileCard)}
                {loading && (
                  <Typography variant="caption" align="center" color="textSecondary">
                    Cargando...
                  </Typography>
                )}
                {hasMore && !loading && (
                  <Button variant="outlined" color="primary" onClick={loadMore} fullWidth>
                    {i18n.t("contacts.loadMore")}
                  </Button>
                )}
              </div>
            )}
          </Paper>

          <Fab color="primary" className={classes.fab} onClick={handleOpenTagModal}>
            <AddIcon />
          </Fab>
        </>
      ) : (
        <>
          <MainHeader>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 16 }}>
              <Title>{i18n.t("tags.title")} ({tags.length})</Title>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <TextField
                  placeholder={i18n.t("contacts.searchPlaceholder")}
                  type="search"
                  value={searchParam}
                  onChange={handleSearch}
                  size="small"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: "gray" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button variant="contained" color="primary" onClick={handleOpenTagModal}>
                  {i18n.t("tags.buttons.add")}
                </Button>
              </div>
            </div>
          </MainHeader>

          <Paper
            className={classes.mainPaper}
            variant="outlined"
            onScroll={handleScroll}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center">{i18n.t("tags.table.id")}</TableCell>
                  <TableCell align="center">{i18n.t("tags.table.name")}</TableCell>
                  <TableCell align="center">{i18n.t("tags.table.contacts")}</TableCell>
                  <TableCell align="center">{i18n.t("tags.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <>
                  {tags.map((tag) => (
                    <TableRow key={tag.id}>
                      <TableCell align="center">{tag.id}</TableCell>
                      <TableCell align="center">
                        <Chip
                          variant="outlined"
                          style={{
                            backgroundColor: tag.color,
                            textShadow: "1px 1px #000",
                            color: "white",
                          }}
                          label={tag.name}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {tag?.contacts?.length}
                        <IconButton
                          size="small"
                          onClick={() => handleShowContacts(tag?.contacts, tag)}
                          disabled={tag?.contacts?.length === 0}
                        >
                          <MoreHoriz />
                        </IconButton>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleEditTag(tag)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setConfirmModalOpen(true);
                            setDeletingTag(tag);
                          }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {loading && Array.from({ length: 5 }).map((_, idx) => (
                    <TableRowSkeleton key={`skeleton-${idx}`} columns={4} />
                  ))}
                </>
              </TableBody>
            </Table>
            {hasMore && (
              <div style={{ display: "flex", justifyContent: "center", padding: 8 }}>
                <Button variant="outlined" color="primary" onClick={loadMore} disabled={loading}>
                  {loading ? i18n.t("contacts.loading") : i18n.t("contacts.loadMore")}
                </Button>
              </div>
            )}
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default Tags;
